'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Zap, Wifi, Shield, CheckCircle, Code, BarChart3, Cpu, Globe, Clock, MapPin, Smartphone } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const edgeComputingData = {
  title: "Edge Computing",
  subtitle: "Bring computation closer to data sources with low-latency, real-time processing solutions that enable faster decision-making and improved user experiences",
  headerGradient: "bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500",
  accentColor: "text-indigo-600",
  buttonColor: "bg-indigo-600 hover:bg-indigo-700",
  
  overview: "Edge Computing brings data processing closer to the source of data generation, reducing latency, improving performance, and enabling real-time decision-making. Our edge computing solutions help organizations process data locally, reduce bandwidth costs, enhance security, and enable new applications that require ultra-low latency and real-time responsiveness.",
  
  capabilities: [
    {
      name: "Real-time Data Processing",
      description: "Process data at the edge with minimal latency for instant insights",
      icon: Zap,
      applications: ["IoT Analytics", "Video Processing", "Sensor Data", "Real-time Monitoring"],
      useCases: ["Instant alerts", "Live analytics", "Immediate responses", "Real-time optimization"]
    },
    {
      name: "Distributed Computing",
      description: "Deploy computing resources across multiple edge locations",
      icon: Globe,
      applications: ["CDN Services", "Micro Data Centers", "Edge Nodes", "Distributed Storage"],
      useCases: ["Global reach", "Load distribution", "Fault tolerance", "Scalable architecture"]
    },
    {
      name: "Low-Latency Applications",
      description: "Enable applications that require ultra-fast response times",
      icon: Clock,
      applications: ["Gaming", "AR/VR", "Autonomous Vehicles", "Industrial Automation"],
      useCases: ["Sub-millisecond response", "Real-time interaction", "Instant feedback", "Critical operations"]
    }
  ],
  
  technologies: [
    {
      category: "Edge Platforms",
      tools: ["AWS IoT Greengrass", "Azure IoT Edge", "Google Cloud IoT Edge", "NVIDIA EGX", "Intel OpenVINO", "EdgeX Foundry"],
      description: "Comprehensive edge computing platforms and frameworks"
    },
    {
      category: "Container Orchestration",
      tools: ["Kubernetes", "K3s", "MicroK8s", "Docker Swarm", "OpenShift", "Rancher"],
      description: "Lightweight container management for edge environments"
    },
    {
      category: "Edge Hardware",
      tools: ["NVIDIA Jetson", "Intel NUC", "Raspberry Pi", "ARM Cortex", "FPGA", "Edge TPU"],
      description: "Specialized hardware for edge computing deployments"
    },
    {
      category: "Communication Protocols",
      tools: ["MQTT", "CoAP", "LoRaWAN", "5G", "WiFi 6", "Bluetooth 5.0"],
      description: "Low-power, efficient communication protocols for edge devices"
    },
    {
      category: "Edge Analytics",
      tools: ["Apache Kafka", "Apache Storm", "TensorFlow Lite", "ONNX Runtime", "OpenCV", "Apache Flink"],
      description: "Real-time analytics and machine learning at the edge"
    },
    {
      category: "Security Solutions",
      tools: ["Secure Boot", "TPM", "Hardware Security Modules", "Zero Trust", "VPN", "Blockchain"],
      description: "Security frameworks and tools for edge environments"
    }
  ],
  
  industries: [
    {
      name: "Manufacturing",
      applications: ["Predictive maintenance", "Quality control", "Process optimization", "Safety monitoring"],
      impact: "50% reduction in downtime, 30% improvement in efficiency"
    },
    {
      name: "Healthcare",
      applications: ["Remote patient monitoring", "Medical imaging", "Emergency response", "Telemedicine"],
      impact: "Real-time patient data, 90% faster diagnosis"
    },
    {
      name: "Retail",
      applications: ["Inventory management", "Customer analytics", "Smart checkout", "Personalization"],
      impact: "40% improvement in inventory accuracy, enhanced customer experience"
    },
    {
      name: "Transportation",
      applications: ["Autonomous vehicles", "Traffic management", "Fleet optimization", "Smart parking"],
      impact: "Sub-10ms response times, 60% reduction in traffic congestion"
    },
    {
      name: "Smart Cities",
      applications: ["Environmental monitoring", "Public safety", "Energy management", "Waste optimization"],
      impact: "25% energy savings, improved public services"
    },
    {
      name: "Agriculture",
      applications: ["Precision farming", "Crop monitoring", "Livestock tracking", "Weather prediction"],
      impact: "30% increase in crop yield, 40% water savings"
    }
  ],
  
  edgeArchitectures: [
    {
      architecture: "Device Edge",
      description: "Computing directly on IoT devices and sensors",
      benefits: ["Ultra-low latency", "Offline capability", "Privacy protection", "Reduced bandwidth"],
      useCases: ["Smart cameras", "Industrial sensors", "Wearable devices", "Autonomous systems"]
    },
    {
      architecture: "Local Edge",
      description: "Computing at local gateways and micro data centers",
      benefits: ["Aggregated processing", "Local storage", "Protocol translation", "Device management"],
      useCases: ["Smart buildings", "Factory floors", "Retail stores", "Hospital units"]
    },
    {
      architecture: "Regional Edge",
      description: "Computing at regional data centers and edge nodes",
      benefits: ["Broader coverage", "Higher capacity", "Content delivery", "Load balancing"],
      useCases: ["Content streaming", "Gaming platforms", "Regional analytics", "Backup services"]
    },
    {
      architecture: "Network Edge",
      description: "Computing at telecom infrastructure and base stations",
      benefits: ["5G integration", "Network optimization", "Service delivery", "Mobile edge computing"],
      useCases: ["Mobile applications", "Network slicing", "Edge CDN", "Telecom services"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Edge Strategy & Assessment",
      description: "Analyze current infrastructure and identify edge computing opportunities",
      details: ["Use case identification", "Latency requirements", "Data flow analysis", "ROI assessment"]
    },
    {
      step: "2", 
      title: "Architecture Design",
      description: "Design distributed edge architecture and select appropriate technologies",
      details: ["Edge topology design", "Technology selection", "Security planning", "Scalability design"]
    },
    {
      step: "3",
      title: "Edge Deployment",
      description: "Deploy edge infrastructure and applications across distributed locations",
      details: ["Hardware deployment", "Software installation", "Network configuration", "Security implementation"]
    },
    {
      step: "4",
      title: "Management & Optimization",
      description: "Monitor, manage, and optimize edge computing infrastructure",
      details: ["Performance monitoring", "Remote management", "Continuous optimization", "Maintenance planning"]
    }
  ],
  
  benefits: [
    "Reduce latency by up to 90%",
    "Decrease bandwidth costs by 60%", 
    "Improve application performance and user experience",
    "Enable real-time decision making and automation",
    "Enhance data privacy and security",
    "Reduce dependency on cloud connectivity",
    "Support offline and intermittent connectivity",
    "Enable new applications requiring ultra-low latency"
  ],
  
  challenges: [
    {
      challenge: "Infrastructure Complexity",
      solution: "Standardized deployment and management frameworks",
      description: "Simplify edge infrastructure deployment and management"
    },
    {
      challenge: "Security & Privacy",
      solution: "Zero-trust security models and encryption",
      description: "Implement comprehensive security across distributed edge nodes"
    },
    {
      challenge: "Resource Constraints",
      solution: "Optimized algorithms and lightweight frameworks",
      description: "Maximize performance within limited edge computing resources"
    },
    {
      challenge: "Network Connectivity",
      solution: "Hybrid cloud-edge architectures and offline capabilities",
      description: "Ensure reliable operation with intermittent connectivity"
    }
  ]
};

export default function EdgeComputingPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${edgeComputingData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(edgeComputingData.title)}
              </h1>
              <p className="text-xl text-indigo-100 mb-8 leading-relaxed">
                {localize(edgeComputingData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["IoT", "5G", "Real-time", "Low Latency"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-indigo-200" />
                    <div className="text-2xl font-bold">90%</div>
                    <div className="text-sm text-indigo-100">{localize("Latency Reduction")}</div>
                  </div>
                  <div className="text-center">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-indigo-200" />
                    <div className="text-2xl font-bold">&lt;10ms</div>
                    <div className="text-sm text-indigo-100">{localize("Response Time")}</div>
                  </div>
                  <div className="text-center">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-indigo-200" />
                    <div className="text-2xl font-bold">60%</div>
                    <div className="text-sm text-indigo-100">{localize("Bandwidth Savings")}</div>
                  </div>
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-indigo-200" />
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-indigo-100">{localize("Edge Monitoring")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${edgeComputingData.accentColor}`}>
              {localize("What is Edge Computing?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(edgeComputingData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core Edge Computing Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {edgeComputingData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-indigo-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-indigo-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Edge Architectures Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Edge Computing Architectures")}
          </motion.h2>
          
          <div className="space-y-8">
            {edgeComputingData.edgeArchitectures.map((arch, index) => (
              <motion.div key={arch.architecture} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(arch.architecture)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(arch.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Key Benefits")}
                        </h4>
                        <ul className="space-y-2">
                          {arch.benefits.map((benefit) => (
                            <li key={benefit} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(benefit)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Common Use Cases")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {arch.useCases.map((useCase) => (
                            <Badge key={useCase} variant="secondary" className="text-xs">
                              {localize(useCase)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Edge Computing Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {edgeComputingData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-indigo-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Edge Computing Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Edge Computing Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {edgeComputingData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-indigo-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {edgeComputingData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-indigo-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our Edge Computing Implementation Process")}
          </h2>
          
          <div className="space-y-8">
            {edgeComputingData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12 border border-indigo-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Edge Computing?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {edgeComputingData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Deploy Edge Computing Solutions?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our edge computing experts help you bring processing closer to your data sources, reduce latency, and enable real-time applications.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${edgeComputingData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Your Edge Journey")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 